/* oxlint-disable require-await, sort-imports, typescript/consistent-type-assertions */
import { mutationOptions } from '@tanstack/react-query';
import type { LocaleCode } from '@repo/shared-config/app/constants/locales';
import { client, camelToSnakeCaseShallow } from '#queries/utils';
import {
  ApiUpdateOrganizationRequest,
  ApiGetOrganizationResponse,
  type ApiOrganizationInput,
  type ApiOrganization,
} from '../register-organization/types';
import {
  ApiUpdateStakeholderRequest,
  ApiUpdateStakeholderResponse,
  type ApiStakeholder,
  type ApiStakeholderInput,
} from '../register-memberships/types';
import { transportNonProtoFields } from '../../utils/transport-non-proto-fields';
import { unwrapResponse } from '../../utils/unwrap-response';
import { registerOrganizationQueries } from '../register-organization/queries';
import { membershipQueries } from '../register-memberships/queries';
import {
  createRegistrationStateV2,
  registrationStateQueries,
  registrationStateV2Queries,
  REGISTRATION_STATES_URL,
  REGISTRATION_STATES_V2_URL,
} from './queries';
import type { ApiRegistrationState, ApiRegistrationStateV2Response } from './types';
import type { RegistrationState } from './states';

export type TransitionAction = 'next' | 'back' | 'reset';

export type ResetOrganizationPayload = Pick<
  ApiOrganizationInput,
  | 'addressCountry'
  | 'companyInfoProviderId'
  | 'declaredNumberOfEmployeeRange'
  | 'initialOrganizationType'
  | 'legalCountry'
  | 'legalFormCreditsafeCode'
  | 'legalNumber'
  | 'name'
  | 'referralCode'
  | 'underRegistration'
>;

interface TransitionParams {
  registrationStateId: string;
  transitionAction: TransitionAction;
  currentState?: RegistrationState;
}

interface PatchOrganizationV1Params {
  registrationStateId: string;
  organizationId: string;
  organizationPayload: Partial<ApiOrganizationInput & ActivityOrganizationPatchPayload>;
  currentState: RegistrationState;
}

export type ActivityOrganizationPatchPayload = Pick<
  ApiOrganization,
  | 'activityDescription'
  | 'businessRelationships'
  | 'businessRelationshipsOther'
  | 'incomingTransfersOrigin'
  | 'outgoingTransfersDestination'
  | 'websiteUrl'
  | 'beneficiariesType'
  | 'beneficiariesTypeOther'
>;

interface PatchStakeholderParams {
  registrationStateId: string;
  stakeholderId: string;
  stakeholderPayload: Partial<ApiStakeholderInput>;
  currentState: RegistrationState;
}

interface PatchContentParams {
  registrationStateId: string;
  schemaValues: Record<string, unknown>;
  currentState: RegistrationState;
}

interface PatchOrganizationV2Params {
  registrationStateId: string;
  organization: Partial<ApiOrganizationInput>;
  currentState: RegistrationState;
}

interface PatchOrganizationV2Response {
  registration_state: ApiRegistrationStateV2Response['registration_state'];
  organization: ApiOrganization;
}

interface ResetOrganizationV1Params {
  registrationStateId: string;
  organizationPayload: ResetOrganizationPayload;
  currentState: RegistrationState;
}

interface CreateV2Params {
  legalCountry: string;
}

function transitionV1() {
  return mutationOptions({
    mutationFn: async ({
      registrationStateId,
      transitionAction,
      currentState,
    }: TransitionParams): Promise<ApiRegistrationState> => {
      const response = await client<{ registration_state: ApiRegistrationState }>(
        `${REGISTRATION_STATES_URL}/${registrationStateId}/transition`,
        {
          method: 'POST',
          body: JSON.stringify({
            transition_action: transitionAction,
            ...(currentState && { current_state: currentState }),
          }),
        }
      );

      return response.registration_state;
    },
    onSuccess: async (data, variables, _result, { client: queryClient }) => {
      queryClient.setQueryData(
        registrationStateQueries.item(variables.registrationStateId).queryKey,
        data
      );
      await queryClient.invalidateQueries({ queryKey: registrationStateV2Queries.items() });
    },
  });
}

function transitionV2(locale: LocaleCode) {
  return mutationOptions({
    mutationFn: async ({
      registrationStateId,
      transitionAction,
      currentState,
    }: TransitionParams): Promise<ApiRegistrationStateV2Response> => {
      return client<ApiRegistrationStateV2Response>(
        `${REGISTRATION_STATES_V2_URL}/${registrationStateId}/transition`,
        {
          method: 'POST',
          body: JSON.stringify({
            transition_action: transitionAction,
            ...(currentState && {
              registration_state: { current_state: currentState },
            }),
          }),
        }
      );
    },
    onSuccess: (data, variables, _result, { client: queryClient }) => {
      // Adapt v2 response to v1 shape for cache compatibility
      queryClient.setQueryData(
        registrationStateQueries.item(variables.registrationStateId).queryKey,
        {
          id: data.registration_state.id,
          organization_id: data.organization.id,
          current_state: data.registration_state.current_state,
          previous_state: data.registration_state.previous_state,
          metadata: {
            registration_flow: data.registration_state.metadata.registration_flow,
            version: data.registration_state.metadata.version,
          },
        }
      );
      queryClient.setQueryData(
        registrationStateV2Queries.item(variables.registrationStateId, locale).queryKey,
        data
      );
    },
  });
}

function createV2(locale: LocaleCode) {
  return mutationOptions({
    mutationFn: async ({
      legalCountry,
    }: CreateV2Params): Promise<ApiRegistrationStateV2Response> => {
      return createRegistrationStateV2(legalCountry);
    },
    onSuccess: async (data, _variables, _result, { client: queryClient }) => {
      queryClient.setQueryData(
        registrationStateV2Queries.item(data.registration_state.id, locale).queryKey,
        data
      );
      await queryClient.invalidateQueries({ queryKey: registrationStateQueries.items() });
    },
  });
}

/**
 * @see {@link file://./../../utils/transport-non-proto-fields.md} for proto toJSON field stripping details
 */
function patchOrganizationV1() {
  return mutationOptions({
    mutationFn: async ({
      registrationStateId,
      organizationId,
      organizationPayload,
      currentState,
    }: PatchOrganizationV1Params): Promise<ApiOrganization> => {
      const payload = organizationPayload as Record<string, unknown>;
      const toJsonResult = ApiUpdateOrganizationRequest.toJSON({
        id: organizationId,
        // partial→full cast; transportNonProtoFields restores stripped null/empty-array fields
        organization: organizationPayload as ApiOrganizationInput,
      }) as Record<string, unknown>;
      const organizationBase = (toJsonResult.organization ?? {}) as Record<string, unknown>;
      // Only apply camelToSnakeCaseShallow to activity fields that are absent from
      // the proto definition. Applying it to the full payload creates duplicate keys
      // with different naming (e.g. address_line1 vs proto's address_line_1).
      const ACTIVITY_ONLY_KEYS = new Set([
        'activityDescription',
        'businessRelationships',
        'businessRelationshipsOther',
        'incomingTransfersOrigin',
        'outgoingTransfersDestination',
        'websiteUrl',
        'beneficiariesType',
        'beneficiariesTypeOther',
      ]);
      const activityFields = Object.fromEntries(
        Object.entries(payload).filter(([key]) => ACTIVITY_ONLY_KEYS.has(key))
      );
      const organizationBody = {
        ...transportNonProtoFields(organizationBase, payload),
        ...camelToSnakeCaseShallow(activityFields),
      };

      const response = await client(
        `${REGISTRATION_STATES_URL}/${registrationStateId}/organizations/${organizationId}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            registration_state: { current_state: currentState },
            organization: organizationBody,
          }),
        }
      );

      return unwrapResponse(ApiGetOrganizationResponse.fromJSON(response), 'organization');
    },
    onSuccess: async (data, variables, _result, { client: queryClient }) => {
      await queryClient.invalidateQueries({
        queryKey: registrationStateQueries.item(variables.registrationStateId).queryKey,
      });
      queryClient.setQueryData(
        registerOrganizationQueries.item(variables.organizationId).queryKey,
        data
      );
      await queryClient.invalidateQueries({ queryKey: registrationStateV2Queries.items() });
    },
  });
}

/**
 * @see {@link file://./../../utils/transport-non-proto-fields.md} for proto toJSON field stripping details
 */
function patchStakeholder() {
  return mutationOptions({
    mutationFn: async ({
      registrationStateId,
      stakeholderId,
      stakeholderPayload,
      currentState,
    }: PatchStakeholderParams): Promise<ApiStakeholder> => {
      const toJsonResult = ApiUpdateStakeholderRequest.toJSON({
        id: stakeholderId,
        // partial→full cast; transportNonProtoFields restores stripped null/empty-array fields
        stakeholder: stakeholderPayload as ApiStakeholderInput,
      }) as Record<string, unknown>;
      const stakeholderBase = (toJsonResult.stakeholder ?? {}) as Record<string, unknown>;
      const stakeholderBody = transportNonProtoFields(
        stakeholderBase,
        // read as plain record for null/empty-array detection
        stakeholderPayload
      );

      const json = await client(
        `${REGISTRATION_STATES_URL}/${registrationStateId}/stakeholders/${stakeholderId}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            registration_state: { current_state: currentState },
            stakeholder: stakeholderBody,
          }),
        }
      );

      return unwrapResponse(ApiUpdateStakeholderResponse.fromJSON(json), 'stakeholder');
    },
    onSuccess: async (data, variables, _result, { client: queryClient }) => {
      await queryClient.invalidateQueries({
        queryKey: registrationStateQueries.item(variables.registrationStateId).queryKey,
      });
      queryClient.setQueryData(membershipQueries.item(variables.stakeholderId).queryKey, data);
      await queryClient.invalidateQueries({ queryKey: registrationStateV2Queries.items() });
    },
  });
}

// Refetch (not just invalidate) so the org data is in sync before navigating to Ember routes
function patchContent(locale: LocaleCode) {
  return mutationOptions({
    mutationFn: async ({
      registrationStateId,
      schemaValues,
      currentState,
    }: PatchContentParams): Promise<ApiRegistrationStateV2Response> => {
      return client<ApiRegistrationStateV2Response>(
        `${REGISTRATION_STATES_V2_URL}/${registrationStateId}/content`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            schema_values: schemaValues,
            registration_state: { current_state: currentState },
          }),
        }
      );
    },
    onSuccess: async (data, _variables, _result, { client: queryClient }) => {
      queryClient.setQueryData(
        registrationStateV2Queries.item(data.registration_state.id, locale).queryKey,
        data
      );
      await queryClient.invalidateQueries({ queryKey: registrationStateQueries.items() });
      await queryClient.refetchQueries({
        queryKey: registerOrganizationQueries.item(data.organization.id).queryKey,
      });
    },
  });
}

/**
 * @see {@link file://./../../utils/transport-non-proto-fields.md} for proto toJSON field stripping details
 */
function patchOrganizationV2() {
  return mutationOptions({
    mutationFn: async ({
      registrationStateId,
      organization,
      currentState,
    }: PatchOrganizationV2Params): Promise<PatchOrganizationV2Response> => {
      const toJsonResult = ApiUpdateOrganizationRequest.toJSON({
        id: '', // we do not really need the ID
        // partial→full cast; transportNonProtoFields restores stripped null/empty-array fields
        organization: organization as ApiOrganizationInput,
      }) as Record<string, unknown>;
      const organizationBase = (toJsonResult.organization ?? {}) as Record<string, unknown>;
      const organizationBody = transportNonProtoFields(organizationBase, organization);

      const response = await client<{
        registration_state: ApiRegistrationStateV2Response['registration_state'];
        organization: unknown;
      }>(`${REGISTRATION_STATES_V2_URL}/${registrationStateId}/organization`, {
        method: 'PATCH',
        body: JSON.stringify({
          organization: organizationBody,
          registration_state: { current_state: currentState },
        }),
      });

      return {
        registration_state: response.registration_state,
        organization: unwrapResponse(ApiGetOrganizationResponse.fromJSON(response), 'organization'),
      };
    },
    onSuccess: async (data, _variables, _result, { client: queryClient }) => {
      await queryClient.invalidateQueries({ queryKey: registrationStateQueries.items() });
      await queryClient.invalidateQueries({ queryKey: registrationStateV2Queries.items() });
      queryClient.setQueryData(
        registerOrganizationQueries.item(data.organization.id).queryKey,
        data.organization
      );
    },
  });
}

function resetOrganizationV1() {
  return mutationOptions({
    mutationFn: async ({
      registrationStateId,
      organizationPayload,
      currentState,
    }: ResetOrganizationV1Params): Promise<ApiOrganization> => {
      const response = await client<{ organization: unknown }>(
        `${REGISTRATION_STATES_URL}/${registrationStateId}/organizations/reset`,
        {
          method: 'POST',
          body: JSON.stringify({
            registration_state: { current_state: currentState },
            organization: camelToSnakeCaseShallow(organizationPayload),
          }),
        }
      );

      return unwrapResponse(ApiGetOrganizationResponse.fromJSON(response), 'organization');
    },
    onSuccess: async (data, _variables, _result, { client: queryClient }) => {
      await queryClient.invalidateQueries({ queryKey: registrationStateQueries.items() });
      await queryClient.invalidateQueries({ queryKey: registrationStateV2Queries.items() });
      queryClient.setQueryData(registerOrganizationQueries.item(data.id).queryKey, data);
    },
  });
}

export const registrationStateMutations = {
  transitionV1,
  transitionV2,
  createV2,
  patchOrganizationV1,
  patchStakeholder,
  patchContent,
  patchOrganizationV2,
  resetOrganizationV1,
};
